import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, Chip, useTheme, useMediaQuery, Portal } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { ContextualStanding } from '@/services/standingsService';
import { ExpandedMatchView } from './ExpandedMatchView';
import { MatchPagination } from './MatchPagination';
import FormChip from './FormChip';
import { UnverifiedDelta } from '@/utils/standingsUtils';

interface TeamInfoDrawerProps {
  open: boolean;
  onClose: () => void;
  team: ContextualStanding | null;
  leagueId: string;
  unverifiedDelta?: UnverifiedDelta;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  disabled: boolean;
  onPageChange: (page: number) => void;
}

const TRANSITION_DURATION = 280;
const TRANSITION_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

const TeamInfoDrawer: React.FC<TeamInfoDrawerProps> = ({ open, onClose, team, leagueId, unverifiedDelta }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [resultsOnly, setResultsOnly] = useState(false);

  // Animation state
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Animation
  useEffect(() => {
    if (open) {
      setVisible(true);
      // rAF waits for DOM flush, second rAF + microtask ensures paint in all browsers
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => setAnimating(true), 0);
        });
      });
    } else {
      setAnimating(false);
      const t = setTimeout(() => setVisible(false), TRANSITION_DURATION);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Lock body scroll while drawer is up (unlocks immediately on dismiss start)
  useEffect(() => {
    if (!animating) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, [animating]);

  // Drag-to-dismiss state
  const paperRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startY: number;
    lastY: number;
    lastTime: number;
    velocity: number;
  } | null>(null);

  const handlePaginationInfo = useCallback((info: PaginationInfo | null) => {
    setPaginationInfo(info);
  }, []);

  // Shared drag-dismiss finish logic (vertical — mobile bottom sheet)
  const finishDrag = useCallback((dy: number, velocity: number) => {
    const paper = paperRef.current;
    if (!paper) return;
    const shouldDismiss = dy > 100 || velocity > 0.5;

    const backdrop = backdropRef.current;
    if (shouldDismiss) {
      setAnimating(false); // unlock scroll immediately
      const speed = Math.max(0.8, Math.min(4, Math.abs(velocity)));
      const remaining = paper.offsetHeight - dy;
      const duration = Math.max(80, Math.min(400, remaining / speed));
      paper.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0, 0.5, 1)`;
      paper.style.transform = `translateY(${paper.offsetHeight}px)`;
      if (backdrop) {
        backdrop.style.transition = `background ${duration}ms ease`;
        backdrop.style.background = 'rgba(0,0,0,0)';
      }
      paper.addEventListener('transitionend', () => {
        paper.style.transition = '';
        paper.style.transform = '';
        if (backdrop) {
          backdrop.style.transition = '';
          backdrop.style.background = '';
        }
        onClose();
      }, { once: true });
    } else {
      paper.style.transition = `transform 250ms cubic-bezier(0.2, 0, 0, 1)`;
      paper.style.transform = '';
      if (backdrop) {
        backdrop.style.transition = `background 250ms ease`;
        backdrop.style.background = 'rgba(0,0,0,0.35)';
      }
      paper.addEventListener('transitionend', () => {
        paper.style.transition = '';
        if (backdrop) {
          backdrop.style.transition = '';
          backdrop.style.background = '';
        }
      }, { once: true });
    }
  }, [onClose]);

  // Animate the drawer closed to the right with a given duration
  const animateCloseX = useCallback((duration: number) => {
    setAnimating(false); // unlock scroll immediately
    const paper = paperRef.current;
    const backdrop = backdropRef.current;
    if (!paper) { onClose(); return; }
    paper.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0, 0.5, 1)`;
    paper.style.transform = `translateX(${paper.offsetWidth}px)`;
    if (backdrop) {
      backdrop.style.transition = `background ${duration}ms ease`;
      backdrop.style.background = 'rgba(0,0,0,0)';
    }
    paper.addEventListener('transitionend', () => {
      paper.style.transition = '';
      if (backdrop) {
        backdrop.style.transition = '';
      }
      onClose();
    }, { once: true });
  }, [onClose]);

  // Shared drag-dismiss finish logic (horizontal — desktop right drawer)
  const finishDragX = useCallback((dx: number, velocity: number) => {
    const paper = paperRef.current;
    if (!paper) return;
    // Treat a tiny drag as a click — animate closed
    if (Math.abs(dx) < 5 && Math.abs(velocity) < 0.1) {
      animateCloseX(TRANSITION_DURATION);
      return;
    }
    const shouldDismiss = dx > 80 || velocity > 0.5;

    const backdrop = backdropRef.current;
    if (shouldDismiss) {
      const remaining = Math.max(1, paper.offsetWidth - dx);
      // Use velocity to determine duration — faster fling = shorter animation
      const velocityPxPerMs = Math.max(0.3, Math.abs(velocity));
      const duration = Math.max(80, Math.min(350, remaining / velocityPxPerMs));
      animateCloseX(duration);
    } else {
      paper.style.transition = `transform 250ms cubic-bezier(0.2, 0, 0, 1)`;
      paper.style.transform = '';
      if (backdrop) {
        backdrop.style.transition = `background 250ms ease`;
        backdrop.style.background = 'rgba(0,0,0,0.35)';
      }
      paper.addEventListener('transitionend', () => {
        paper.style.transition = '';
        if (backdrop) {
          backdrop.style.transition = '';
          backdrop.style.background = '';
        }
      }, { once: true });
    }
  }, [animateCloseX]);

  // Track velocity during drag movement (vertical)
  const trackMove = useCallback((clientY: number) => {
    if (!dragRef.current || !paperRef.current) return;
    const now = performance.now();
    const dt = now - dragRef.current.lastTime;
    if (dt > 0) {
      const instantV = (clientY - dragRef.current.lastY) / dt;
      dragRef.current.velocity = dragRef.current.velocity * 0.3 + instantV * 0.7;
    }
    dragRef.current.lastY = clientY;
    dragRef.current.lastTime = now;
    const dy = Math.max(0, clientY - dragRef.current.startY);
    paperRef.current.style.transform = `translateY(${dy}px)`;
    // Fade backdrop proportionally to drag distance
    if (backdropRef.current && paperRef.current) {
      const progress = Math.max(0, 1 - dy / paperRef.current.offsetHeight);
      backdropRef.current.style.background = `rgba(0,0,0,${(0.35 * progress).toFixed(3)})`;
    }
  }, []);

  // Track velocity during drag movement (horizontal)
  const trackMoveX = useCallback((clientX: number) => {
    if (!dragRef.current || !paperRef.current) return;
    const now = performance.now();
    const dt = now - dragRef.current.lastTime;
    if (dt > 0) {
      const instantV = (clientX - dragRef.current.lastY) / dt; // lastY reused as lastPos
      dragRef.current.velocity = dragRef.current.velocity * 0.3 + instantV * 0.7;
    }
    dragRef.current.lastY = clientX;
    dragRef.current.lastTime = now;
    const dx = Math.max(0, clientX - dragRef.current.startY); // startY reused as startPos
    paperRef.current.style.transform = `translateX(${dx}px)`;
    if (backdropRef.current && paperRef.current) {
      const progress = Math.max(0, 1 - dx / paperRef.current.offsetWidth);
      backdropRef.current.style.background = `rgba(0,0,0,${(0.35 * progress).toFixed(3)})`;
    }
  }, []);

  // Initialize drag state
  const startDrag = useCallback((pos: number) => {
    dragRef.current = {
      startY: pos,
      lastY: pos,
      lastTime: performance.now(),
      velocity: 0,
    };
    if (paperRef.current) {
      paperRef.current.style.transition = 'none';
    }
    if (backdropRef.current) {
      backdropRef.current.style.transition = 'none';
    }
  }, []);

  // Mouse-based drag-to-dismiss (vertical — mobile header)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    startDrag(e.clientY);

    const handleMouseMove = (ev: MouseEvent) => trackMove(ev.clientY);
    const handleMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (!dragRef.current) return;
      const dy = ev.clientY - dragRef.current.startY;
      const velocity = dragRef.current.velocity;
      dragRef.current = null;
      finishDrag(dy, velocity);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [startDrag, trackMove, finishDrag]);

  // Mouse-based drag-to-dismiss (horizontal — desktop handle)
  const handleMouseDownX = useCallback((e: React.MouseEvent) => {
    startDrag(e.clientX);

    const handleMouseMove = (ev: MouseEvent) => trackMoveX(ev.clientX);
    const handleMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startY;
      const velocity = dragRef.current.velocity;
      dragRef.current = null;
      finishDragX(dx, velocity);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [startDrag, trackMoveX, finishDragX]);

  // Touch-based drag-to-dismiss for mobile (header)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startDrag(e.touches[0].clientY);

    const handleTouchMove = (ev: TouchEvent) => {
      ev.preventDefault();
      trackMove(ev.touches[0].clientY);
    };
    const handleTouchEnd = (ev: TouchEvent) => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      if (!dragRef.current) return;
      const clientY = ev.changedTouches[0].clientY;
      const dy = clientY - dragRef.current.startY;
      const velocity = dragRef.current.velocity;
      dragRef.current = null;
      finishDrag(dy, velocity);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [startDrag, trackMove, finishDrag]);

  // Touch-based drag-to-dismiss for desktop handle (horizontal)
  const handleTouchStartX = useCallback((e: React.TouchEvent) => {
    startDrag(e.touches[0].clientX);

    const handleTouchMove = (ev: TouchEvent) => {
      ev.preventDefault();
      trackMoveX(ev.touches[0].clientX);
    };
    const handleTouchEnd = (ev: TouchEvent) => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      if (!dragRef.current) return;
      const clientX = ev.changedTouches[0].clientX;
      const dx = clientX - dragRef.current.startY;
      const velocity = dragRef.current.velocity;
      dragRef.current = null;
      finishDragX(dx, velocity);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [startDrag, trackMoveX, finishDragX]);

  // Pull-to-dismiss from scroll area when scrolled to top
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleScrollTouchStart = useCallback((e: React.TouchEvent) => {
    const scrollEl = scrollRef.current;
    if (!scrollEl || scrollEl.scrollTop > 0) return;

    const startY = e.touches[0].clientY;
    let dragging = false;
    let hasScrolled = false;

    const handleTouchMove = (ev: TouchEvent) => {
      // If content ever scrolled, don't allow drag-to-dismiss for this touch
      if (!dragging && scrollEl.scrollTop > 0) {
        hasScrolled = true;
      }
      const dy = ev.touches[0].clientY - startY;
      if (!dragging) {
        if (!hasScrolled && dy > 0 && scrollEl.scrollTop <= 0) {
          dragging = true;
          // Lock scroll area so swiping back up doesn't scroll
          scrollEl.style.overflow = 'hidden';
          startDrag(ev.touches[0].clientY);
        } else {
          return;
        }
      }
      ev.preventDefault();
      trackMove(ev.touches[0].clientY);
    };
    const handleTouchEnd = (ev: TouchEvent) => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      // Restore scroll area
      if (dragging) {
        scrollEl.style.overflow = '';
      }
      if (!dragging || !dragRef.current) return;
      const clientY = ev.changedTouches[0].clientY;
      const dy = clientY - dragRef.current.startY;
      const velocity = dragRef.current.velocity;
      dragRef.current = null;
      finishDrag(dy, velocity);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [startDrag, trackMove, finishDrag]);

  if (!team || !visible) return null;

  return (
    <Portal>
      {/* Backdrop */}
      <Box
        ref={backdropRef}
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: isDesktop ? 0 : 56,
          zIndex: isDesktop ? theme.zIndex.drawer : 1099,
          background: animating ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0)',
          transition: `background ${TRANSITION_DURATION}ms ease`,
          display: 'flex',
          alignItems: isDesktop ? 'stretch' : 'flex-end',
          justifyContent: isDesktop ? 'flex-end' : 'center',
        }}
      >
        {/* Paper */}
        <Box
          ref={paperRef}
          onClick={(e) => e.stopPropagation()}
          sx={isDesktop ? {
            width: 600,
            height: `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
            marginTop: `${theme.mixins.toolbar.minHeight}px`,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'row',
            overscrollBehavior: 'contain',
            transform: animating ? 'translateX(0)' : 'translateX(100%)',
            transition: `transform ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`,
            boxShadow: theme.shadows[16],
          } : {
            width: '100%',
            height: '85vh',
            '@supports (height: 1dvh)': {
              height: '85dvh',
            },
            bgcolor: 'background.paper',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            pb: '56px',
            mb: '-56px',
            overscrollBehavior: 'contain',
            transform: animating ? 'translateY(0)' : 'translateY(100%)',
            transition: `transform ${TRANSITION_DURATION}ms ${TRANSITION_EASING}`,
          }}
        >
          {/* Desktop: vertical drag handle on the left edge */}
          {isDesktop && (
            <Box
              onMouseDown={(e) => { e.stopPropagation(); handleMouseDownX(e); }}
              onTouchStart={(e) => { e.stopPropagation(); handleTouchStartX(e); }}
              sx={{
                width: 20,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                touchAction: 'none',
                userSelect: 'none',
                borderRight: '1px solid',
                borderColor: 'divider',
                '&:hover .drag-pill': { bgcolor: 'grey.500' },
              }}
            >
              <Box className="drag-pill" sx={{ width: 4, height: 36, borderRadius: 2, bgcolor: 'grey.300', transition: 'background-color 150ms' }} />
            </Box>
          )}

          {/* Content column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
            {/* Fixed header */}
            <Box
              {...(!isDesktop ? { onTouchStart: handleTouchStart, onMouseDown: handleMouseDown } : {})}
              sx={{
                flexShrink: 0,
                ...(!isDesktop ? { touchAction: 'none', userSelect: 'none', cursor: 'grab', '&:active': { cursor: 'grabbing' } } : {}),
              }}
            >
              {/* Drag handle (mobile only) */}
              {!isDesktop && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pt: 1.5,
                    pb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: 'grey.400',
                    }}
                  />
                </Box>
              )}

              {/* Team info */}
              <Box sx={{ px: 2, pb: 1.5, pt: isDesktop ? 2 : 0, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#4caf50',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      flexShrink: 0,
                    }}
                  >
                    {team.position}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                    {team.teamName}
                  </Typography>
                </Box>

                {/* Record + GD */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {team.won}{unverifiedDelta && unverifiedDelta.won > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({unverifiedDelta.won})</Box>}W-{team.drawn}{unverifiedDelta && unverifiedDelta.drawn > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({unverifiedDelta.drawn})</Box>}D-{team.lost}{unverifiedDelta && unverifiedDelta.lost > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({unverifiedDelta.lost})</Box>}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {team.goalsFor}-{team.goalsAgainst} ({team.goalDifference > 0 ? '+' : ''}{team.goalDifference})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {team.points} pts
                  </Typography>
                </Box>

                {/* Form + filter */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  {team.form ? <FormChip form={team.form.slice(-10)} /> : <Box />}
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 16 }} />}
                    label="Results"
                    size="small"
                    variant={resultsOnly ? 'filled' : 'outlined'}
                    color={resultsOnly ? 'primary' : 'default'}
                    onClick={() => setResultsOnly(!resultsOnly)}
                    sx={{ fontSize: '0.75rem', height: 26, minWidth: 90 }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Scrollable match history */}
            <Box ref={scrollRef} onTouchStart={!isDesktop ? handleScrollTouchStart : undefined} sx={{ flex: 1, overflowY: 'auto', minHeight: 0, overscrollBehavior: 'contain' }}>
              <ExpandedMatchView
                teamName={team.teamName}
                leagueId={leagueId}
                division={team.division}
                teamStats={{
                  won: team.won,
                  drawn: team.drawn,
                  lost: team.lost,
                  goalsFor: team.goalsFor,
                  goalsAgainst: team.goalsAgainst,
                  goalDifference: team.goalDifference,
                }}
                hideStats
                hideUpcoming={resultsOnly}
                twoColumn
                hidePagination
                onPaginationInfo={handlePaginationInfo}
              />
            </Box>

            {/* Fixed pagination footer */}
            {paginationInfo && (
              <Box sx={{
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                py: 0.5,
              }}>
                <MatchPagination
                  currentPage={paginationInfo.currentPage}
                  totalPages={paginationInfo.totalPages}
                  onPageChange={paginationInfo.onPageChange}
                  disabled={paginationInfo.disabled}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Portal>
  );
};

export default TeamInfoDrawer;
